import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';
import { useUserDetailsState } from './useUserDetailsState';
import { createUserDetailsHandlers } from './useUserDetailsHandlers';
import { UserDetailsHeader } from './UserDetailsHeader';
import { OverviewCards } from './OverviewCards';
import { AccountInfoCard } from './AccountInfoCard';
import { SubscriptionCard } from './SubscriptionCard';
import { AdminOverridesCard } from './AdminOverridesCard';
import { MarshmallowCard } from './MarshmallowCard';
import { WebsitesCard } from './WebsitesCard';
import { RecentActivityCard } from './RecentActivityCard';
import { SidebarCards } from './SidebarCards';

export const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const state = useUserDetailsState();
  const handlers = createUserDetailsHandlers({ userId, state });
  const [isLaunchingRoary, setIsLaunchingRoary] = useState(false);

  useEffect(() => {
    handlers.fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleBuildWithRoary = async () => {
    if (!userId) return;
    try {
      setIsLaunchingRoary(true);
      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) throw new Error('No authentication token found');
      const response = await adminApi.startMimicSession(userId);
      localStorage.setItem('originalAdminToken', currentToken);
      localStorage.setItem('mimickedUser', JSON.stringify(response.mimickedUser));
      localStorage.setItem('authToken', response.token);
      window.location.href = '/dashboard?roary=open';
    } catch (error: any) {
      console.error('Failed to launch Roary as user:', error);
      alert(error.message || 'Failed to start Roary session');
      setIsLaunchingRoary(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminDashboardHeader onLogout={logout} />
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading User Details</h2>
            <p className="text-gray-600 mb-4">{state.error}</p>
            <button onClick={() => navigate('/admin')} className="btn btn-primary">
              Back to Admin Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!state.userDetails) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminDashboardHeader onLogout={logout} />
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">👤</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">User Not Found</h2>
            <p className="text-gray-600 mb-4">The requested user could not be found.</p>
            <button onClick={() => navigate('/admin')} className="btn btn-primary">
              Back to Admin Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    user,
    auth,
    statistics,
    websites,
    media,
    components,
    pageTemplates,
    siteTemplates,
    recentActivity,
    insights,
    subscription,
  } = state.userDetails;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminDashboardHeader onLogout={logout} />
      <UserDetailsHeader
        user={user}
        onEditUser={() => navigate('/admin', { state: { editUserId: user.id } })}
        onBuildWithRoary={handleBuildWithRoary}
        isLaunchingRoary={isLaunchingRoary}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <OverviewCards statistics={statistics} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            <AccountInfoCard auth={auth} statistics={statistics} insights={insights} />
            <SubscriptionCard
              subscription={subscription}
              userId={userId}
              onPlanChanged={handlers.fetchUserDetails}
            />
            <AdminOverridesCard
              subscription={subscription}
              state={state}
              onOpenForm={handlers.handleOpenOverrideForm}
              onSave={handlers.handleSaveOverrides}
              onClear={handlers.handleClearOverrides}
            />
            <MarshmallowCard userId={userId} />
            <WebsitesCard websites={websites} />
            <RecentActivityCard recentActivity={recentActivity} />
          </div>

          {/* Right Column */}
          <SidebarCards
            media={media}
            components={components}
            pageTemplates={pageTemplates}
            siteTemplates={siteTemplates}
            insights={insights}
          />
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
